import React, { FC, PropsWithChildren, useEffect, useRef, forwardRef } from 'react';
import PureLink, { PureLinkProps } from '../../../common/Link/PureLink';
import { classes } from '../../../../lib/utils/cssUtils';
import {
  useGetDetailsLink,
  LearningDetailsLinkParams,
  DetailsLinkDestination,
} from '../../../../lib/hooks/useGetDetailsLink';
import { Role, useRole } from '../../../../lib/context/SessionContext';
import { TRAINING_DETAILS_CLICK } from '../../../../lib/constants/attributesAnalytics';
import Button, { ButtonProps } from '../../../common/Button/Button';
import { useTrainingActions } from '../../../../lib/api/swrApi';
import PureRichText from '../../../common/PureRichText/PureRichText';
import Loader from '../../../common/Loader/Loader';
import styles from './LearningLink.module.scss';
import { CourseCardProps } from '../CourseCard/CourseCard.types';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';
import { Api } from '../../../../lib/types/apiSpec';
import { isTrainingType } from '../../../../lib/utils/trainings/typesChecking';
import LearningDetailsLabel from '../LearningDetailsLabel/LearningDetailsLabel';

export type LearningLinkProps = {
  to?: DetailsLinkDestination;
  card?: boolean;
  status?: Api.TrainingStatus;
  classNames?: {
    loader?: string;
    notAvailableMessage?: string;
  };
  ContentElement?: FC<
    PropsWithChildren<{
      onClick?: (event: React.MouseEvent) => Promise<void>;
    }>
  >;
  params?: LearningDetailsLinkParams;
} & Omit<PureLinkProps, 'href'> &
  Pick<CourseCardProps, 'id' | 'position' | 'type' | 'name' | 'eventRegistrationUrl' | 'eventId'> & {
    checkIfAvailable?: boolean;
    notAvailableMessage?: string;
    originalId?: number | string;
    eventOriginalId?: number | string;
  };

export const getCustomLinkElement =
  ({ asButton = true, label }: { asButton?: boolean; label?: string } = {}) =>
  ({ children, ...rest }: ButtonProps) => {
    const CustomElement = asButton ? Button : 'span';

    return (
      <CustomElement {...rest} className={styles.previewBtn}>
        {label ?? children}
      </CustomElement>
    );
  };

const LearningLink = forwardRef<HTMLAnchorElement, LearningLinkProps>(
  (
    {
      type,
      status,
      id,
      originalId,
      name,
      card,
      ContentElement,
      className,
      classNames,
      checkIfAvailable = false,
      notAvailableMessage,
      to,
      params,
      children,
      eventRegistrationUrl,
      eventId,
      eventOriginalId,
      ...rest
    },
    ref,
  ) => {
    const { triggerSnackbarError } = useErrorMapping();
    const internalRef = useRef<HTMLAnchorElement>(null);
    const role = useRole();
    const shouldCheck = role !== Role.GUEST && checkIfAvailable;
    const {
      data,
      isValidating: areActionsValidating,
      error: actionsError,
    } = useTrainingActions({
      id,
      type: type === Api.LearningType.LearningPath ? Api.TrainingType.LearningPath : Api.TrainingType.Course,
      shouldFetch: shouldCheck && (isTrainingType(type) || (type === Api.EventType.Event && !eventRegistrationUrl)),
    });

    const {
      isLoading: isLinkLoading,
      onClick: onLinkClick,
      href,
    } = useGetDetailsLink({
      type,
      to,
      id,
      eventId,
      shouldSelfAssign: shouldCheck ? data?.shouldSelfAssign : to === 'litmos',
      params,
    })({
      originalId,
      ref: ref ?? internalRef,
      name,
      eventRegistrationUrl,
      eventOriginalId,
    });

    const isGeneralLoading = (shouldCheck && areActionsValidating) || isLinkLoading;

    useEffect(() => {
      if (actionsError) {
        triggerSnackbarError(actionsError);
      }
    }, [actionsError]);

    if (shouldCheck && !isGeneralLoading && !actionsError && !data?.canStart && !eventRegistrationUrl) {
      return <PureRichText richtextClass={classNames?.notAvailableMessage} richtext={notAvailableMessage || ''} />;
    }

    if (actionsError) {
      return null;
    }

    return (
      <>
        <PureLink
          {...rest}
          className={classes(className, { [styles.hide]: isGeneralLoading, [styles.link]: !isGeneralLoading })}
          href={href}
          ref={ref ?? internalRef}
          title={name}
          {...{ [TRAINING_DETAILS_CLICK.CATEGORY]: type, [TRAINING_DETAILS_CLICK.PRODUCT]: name }}
          forceNativeAnchor={to === DetailsLinkDestination.LITMOS}
        >
          {ContentElement ? (
            <ContentElement onClick={onLinkClick}>
              <LearningDetailsLabel
                type={type}
                status={status}
                card={card}
                eventRegistrationUrl={eventRegistrationUrl}
              />
            </ContentElement>
          ) : (
            children ?? <LearningDetailsLabel type={type} status={status} card={card} onClick={onLinkClick} />
          )}
        </PureLink>
        {isGeneralLoading && <Loader containerProps={{ className: classNames?.loader ?? styles.loaderContainer }} />}
      </>
    );
  },
);

LearningLink.displayName = 'LearningLink';

export default LearningLink;
